import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getPermissionNames, RESOURCE_TYPE_PORTAL } from "@Helpers/constants";
import type { RootState } from "@Reducers/index";

/** The caller's grants over SHARED sidebar layouts. Personal layouts need none of these. */
export interface SidebarLayoutPermissions {
  /** Create as Shared, Move to Shared, and Duplicate a shared layout as shared. */
  canCreateShared: boolean;
  /** Configure and rename ANY shared layout, whoever created it. */
  canEditShared: boolean;
  /** Delete ANY shared layout, whoever created it (the in-use guard still applies). */
  canDeleteShared: boolean;
}

/**
 * Reads the three shared-layout portal permissions off the `userPermissions` slice, which `Router`
 * already populates for every authenticated session — so this adds no fetch and no loading state.
 *
 * Permission NAMES rather than the `portalSettingInfo` capability object: the decision is literally
 * "is permission X granted", and going through `getPermissionNames()` makes a typo a compile error.
 * No Super Admin / Admin special-case — those roles carry the three names in their own permission
 * arrays, which is what keeps the permissions non-overlapping for admins as much as anyone.
 */
export function useSidebarLayoutPermissions(): SidebarLayoutPermissions {
  const permissionsSlice = useSelector((state: RootState) => state.userPermissions);

  return useMemo(() => {
    // `UserPermissionsData` is a union of a per-resource-type record and a flat string array, so both
    // arms are handled rather than asserted away.
    const granted: string[] = Array.isArray(permissionsSlice)
      ? permissionsSlice
      : (permissionsSlice[RESOURCE_TYPE_PORTAL]?.permissions ?? []);
    const permissionNames = getPermissionNames();
    return {
      canCreateShared: granted.includes(permissionNames.CREATE_SHARED_LAYOUTS),
      canEditShared: granted.includes(permissionNames.EDIT_SHARED_LAYOUT),
      canDeleteShared: granted.includes(permissionNames.DELETE_SHARED_LAYOUT),
    };
  }, [permissionsSlice]);
}
